import { useState } from 'react';
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon, KeyRoundIcon, PencilIcon } from 'lucide-react';
import {
  Avatar,
  BackLink,
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  PageEyebrow,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { EditUserDialog } from '@/components/EditUserDialog';
import { SalesforceConnectionSection } from '@/components/SalesforceConnectionSection';
import { SCHEDULING_LINK_ROLES, SchedulingLinksSection } from '@/pages/profile-scheduling-links';
import { RolesSection } from '@/pages/profile-roles-section';
import { accountTypeForTier } from '@/lib/roles';
import { errorMessage } from '@/lib/api';
import { formatEventDate } from '@/lib/event-time';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  avatarSrc,
  personName,
  readMyIdentity,
  readPermissionCatalog,
  readUser,
  readUserEvents,
  readUserLogins,
  readUserPermissions,
  setUserPermission,
  type ProfileEventRow,
} from '@/lib/events';
import { useQueryClient } from '@tanstack/react-query';

/**
 * One person's profile — the staff management view.
 *
 * Beyond who they are, it shows what they have done: the events they are on
 * (upcoming and past) and their sign-in history, mirroring the legacy profile
 * so staff can manage a facilitator from one screen.
 *
 * @returns The profile page.
 */
export function ProfilePage(): React.ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const [params] = useSearchParams();
  const from = params.get('from');
  const backTo = from ? `/people?page=${from}` : '/people';
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const person = useQuery({
    queryKey: ['user', userId],
    queryFn: ({ signal }) => readUser(userId ?? '', signal),
    enabled: userId !== undefined,
  });

  const me = useQuery({
    queryKey: ['staff-me'],
    queryFn: ({ signal }) => readMyIdentity(signal),
  });

  if (person.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={4} columns={['w-[30%]', 'w-[40%]']} label="Loading profile…" />
      </section>
    );
  }
  if (person.isError) {
    return <ErrorNotice title="Profile unavailable" message={errorMessage(person.error)} />;
  }

  const data = person.data;
  const name = personName(data);
  // Admins edit anyone; everyone may edit themselves.
  const isAdmin = me.data !== undefined && ['admin', 'superadmin'].includes(me.data.role);
  const isSelf = me.data !== undefined && me.data.id === userId;
  const canEdit = isAdmin || isSelf;
  // Mirrors the API's gate on /users/:id/logins (INTERNAL_ROLES) — anyone else
  // would only see the request 403, so don't render the section at all.
  const canSeeLogins =
    me.data !== undefined && ['internal', 'admin', 'superadmin'].includes(me.data.role);

  return (
    <section className="animate-portal-rise">
      <BackLink to={backTo}>Directory</BackLink>

      <div className="mt-3 mb-[27px] flex flex-wrap items-center gap-4">
        <Avatar name={name} size={56} imageUrl={avatarSrc(data)} />
        <div className="min-w-0 flex-1">
          <PageEyebrow>PEOPLE</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1px]">{name}</h1>
          <div className="mt-1 flex items-center gap-2">
            {data.role ? (
              <Pill tone={data.role === 'client' ? 'brand' : 'neutral'}>{data.role}</Pill>
            ) : null}
            {data.jobTitle || data.company ? (
              <span className="text-portal-small text-portal-muted">{[data.jobTitle, data.company].filter(Boolean).join(' · ')}</span>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden className="size-4" /> {isSelf && !isAdmin ? 'Edit my profile' : 'Edit user'}
          </Button>
        ) : null}
      </div>

      {canEdit ? (
        <EditUserDialog
          user={data}
          mode={isAdmin ? 'admin' : 'self'}
          open={editing}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void queryClient.invalidateQueries({ queryKey: ['user', userId] });
            void queryClient.invalidateQueries({ queryKey: ['people'] });
            // The shell's account control renders the identity avatar —
            // refresh it so a new profile picture shows without a reload.
            if (isSelf) void queryClient.invalidateQueries({ queryKey: ['my-identity'] });
          }}
        />
      ) : null}

      <Panel className="p-5">
        <FactList
          columns={2}
          showEmpty
          facts={[
            { label: 'Email', value: data.email },
            { label: 'Phone', value: data.phone },
            { label: 'Job title', value: data.jobTitle },
            { label: 'Company', value: data.company },
            { label: 'Account type', value: data.accountType },
            {
              label: 'Location',
              value: [data.address?.city, data.address?.state].filter(Boolean).join(', '),
            },
            {
              label: 'Also reachable at',
              value: data.associatedEmails?.filter((email) => email !== data.email).join(', '),
            },
          ]}
        />
      </Panel>

      {data.role && SCHEDULING_LINK_ROLES.has(data.role) ? (
        <SchedulingLinksSection
          person={data}
          {...(isSelf ? { mode: 'self' as const } : isAdmin ? { mode: 'admin' as const } : {})}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: ['user', userId] })}
        />
      ) : null}

      {/* Your own Salesforce login. Renders nothing unless the API admits you. */}
      {isSelf ? <SalesforceConnectionSection /> : null}

      {userId && !isSelf ? <RolesSection userId={userId} tier={data.role} /> : null}

      {userId && !isSelf ? <PermissionsSection userId={userId} tier={data.role} rbacEnabled={me.data?.rbacEnabled === true} /> : null}

      {userId ? (
        <>
          <EventHistorySection userId={userId} scope="upcoming" title="Upcoming events" />
          <EventHistorySection userId={userId} scope="past" title="Past events" />
          {canSeeLogins ? <LoginSection userId={userId} /> : null}
        </>
      ) : null}
    </section>
  );
}

/**
 * The permission-set management card: for each permission the viewer may
 * grant, a toggle over this user's grant. Renders nothing when the viewer
 * can grant none, so it is invisible to staff who aren't delegators.
 *
 * @param props - The target user id.
 * @returns The permissions card, or null.
 */
function PermissionsSection({
  userId,
  tier,
  rbacEnabled,
}: {
  userId: string;
  /** The person's stored tier: only permissions their account type may hold are offered. */
  tier: string | undefined;
  /** With roles on, these are the extras a person holds outside their roles. */
  rbacEnabled: boolean;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: ({ signal }) => readPermissionCatalog(signal),
    staleTime: 5 * 60_000,
  });
  const held = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: ({ signal }) => readUserPermissions(userId, signal),
  });

  const setPermission = useMutation({
    mutationFn: ({ permission, grant }: { permission: string; grant: boolean }) =>
      setUserPermission(userId, permission, grant),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] }),
  });

  // The API refuses a permission this person's account type may not hold.
  const accountType = accountTypeForTier(tier);
  const grantable = (catalog.data ?? []).filter(
    (entry) => entry.canGrant && entry.assignableTo.includes(accountType),
  );
  // Nothing to manage → the card doesn't exist for this viewer.
  if (!catalog.isPending && grantable.length === 0) return null;

  const heldSet = new Set(held.data?.permissions ?? []);
  const implicit = held.data?.implicit === true;

  return (
    <div className="mt-6">
      <h2 className="mb-1 text-portal-section font-bold tracking-[-0.4px]">
        {rbacEnabled ? 'Extra permissions' : 'Permissions'}
      </h2>
      <p className="mb-3 text-portal-small text-portal-muted">
        {rbacEnabled
          ? 'Given to this person directly, on top of what their roles give them. You can grant only permissions you hold yourself.'
          : 'Access to sensitive surfaces, granted per person. You can grant only permissions you hold yourself.'}
      </p>
      <Panel className="divide-y divide-portal-line p-0">
        {catalog.isPending || held.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={1} columns={['w-[60%]', 'w-[20%]']} label="Loading permissions…" />
          </div>
        ) : implicit ? (
          <p className="p-5 text-portal-small text-portal-muted">
            This user is a super admin and holds every permission implicitly.
          </p>
        ) : (
          grantable.map((entry) => {
            const on = heldSet.has(entry.key);
            const pending =
              setPermission.isPending && setPermission.variables?.permission === entry.key;
            return (
              <div key={entry.key} className="flex items-center justify-between gap-4 p-5">
                <span className="min-w-0">
                  <span className="block text-portal-small font-bold">{entry.label}</span>
                  <span className="mt-0.5 block text-portal-micro text-portal-muted">
                    {entry.description}
                  </span>
                </span>
                <Button
                  variant={on ? 'secondary' : 'primary'}
                  onClick={() => setPermission.mutate({ permission: entry.key, grant: !on })}
                  disabled={pending}
                >
                  {pending ? 'Saving…' : on ? 'Revoke' : 'Grant'}
                </Button>
              </div>
            );
          })
        )}
        {setPermission.isError ? (
          <p className="p-4 text-portal-micro font-semibold text-red-600">
            Could not update the permission. You may not be allowed to grant this one.
          </p>
        ) : null}
      </Panel>
    </div>
  );
}

const EVENT_HEADERS = ['Contract', 'Date', 'Client', 'Program', 'Role', 'Participants', 'Location'];

/** The profile's three history tables search independently, each under its own URL key. */
const PROFILE_SEARCH_KEYS = { upcoming: 'upcomingQ', past: 'pastQ', logins: 'loginsQ' } as const;

/**
 * A paginated table of the user's events for one scope (upcoming or past).
 *
 * @param props - The user id, the scope, and the section title.
 * @returns The events section.
 */
function EventHistorySection({
  userId,
  scope,
  title,
}: {
  readonly userId: string;
  readonly scope: 'upcoming' | 'past';
  readonly title: string;
}): React.ReactElement {
  const [page, setPage] = useState(1);
  const { search, setSearch, debounced } = useUrlSearch(PROFILE_SEARCH_KEYS[scope]);
  const query = useQuery({
    queryKey: ['user-events', userId, scope, page, debounced],
    queryFn: ({ signal }) => readUserEvents(userId, { scope, page, ...(debounced ? { search: debounced } : {}) }, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mt-8">
      <h2 className="mb-3 text-portal-section font-bold">{title}</h2>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          label={`Search ${title.toLowerCase()}`}
          placeholder="Contract, client, program or place"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
      </div>
      {query.isPending ? (
        <SkeletonTable rows={3} columns={['w-[15%]', 'w-[20%]', 'w-[20%]', 'w-[20%]']} label="Loading events…" />
      ) : query.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(query.error)} />
      ) : query.data.rows.length === 0 && debounced !== '' ? (
        <Panel>
          <EmptyState title="No events match" message="Try a different search." />
        </Panel>
      ) : query.data.rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<CalendarDaysIcon aria-hidden className="size-6" />}
            title={scope === 'upcoming' ? 'No upcoming events' : 'No past events'}
            message={
              scope === 'upcoming'
                ? 'Events this person is assigned to will appear here.'
                : 'Delivered events this person facilitated or worked will appear here.'
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable headers={EVENT_HEADERS} minWidth={720}>
            {query.data.rows.map((row) => (
              <EventRow key={row.id} row={row} />
            ))}
          </DataTable>
          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            onChange={setPage}
            summary={`${query.data.total} event${query.data.total === 1 ? '' : 's'}`}
          />
        </>
      )}
    </div>
  );
}

/** One event row, linking to the event. */
function EventRow({ row }: { readonly row: ProfileEventRow }): React.ReactElement {
  return (
    <tr className="border-t border-portal-line">
      <td className="p-3">
        <Link
          to={`/events/${row.id}`}
          className="text-portal-small font-bold text-brand hover:underline"
        >
          {row.contractNumber ?? 'Event'}
        </Link>
      </td>
      <td className="p-3 text-portal-small">
        {formatEventDate(row, 'medium') ?? '—'}
        {row.time ? <span className="block text-portal-micro text-portal-muted">{row.time}</span> : null}
      </td>
      <td className="p-3 text-portal-small">{row.client ?? '—'}</td>
      <td className="p-3 text-portal-small">{row.program ?? '—'}</td>
      <td className="p-3">
        {row.role ? (
          <Pill tone={row.role === 'Lead facilitator' ? 'brand' : 'neutral'}>{row.role}</Pill>
        ) : (
          '—'
        )}
      </td>
      <td className="p-3 text-portal-small">{row.participants ?? '—'}</td>
      <td className="p-3 text-portal-small">{row.location ?? '—'}</td>
    </tr>
  );
}

/**
 * A paginated table of the user's sign-in history.
 *
 * @param props - The user id.
 * @returns The logins section.
 */
function LoginSection({ userId }: { readonly userId: string }): React.ReactElement {
  const [page, setPage] = useState(1);
  const { search, setSearch, debounced } = useUrlSearch(PROFILE_SEARCH_KEYS.logins);
  const query = useQuery({
    queryKey: ['user-logins', userId, page, debounced],
    queryFn: ({ signal }) => readUserLogins(userId, { page, ...(debounced ? { search: debounced } : {}) }, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mt-8">
      <h2 className="mb-3 text-portal-section font-bold">Sign-in history</h2>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search sign-in history"
          placeholder="URL or IP address"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
      </div>
      {query.isPending ? (
        <SkeletonTable rows={3} columns={['w-[20%]', 'w-[30%]', 'w-[20%]']} label="Loading sign-ins…" />
      ) : query.isError ? (
        <ErrorNotice title="Sign-in history unavailable" message={errorMessage(query.error)} />
      ) : query.data.rows.length === 0 && debounced !== '' ? (
        <Panel>
          <EmptyState title="No sign-ins match" message="Try a different search." />
        </Panel>
      ) : query.data.rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<KeyRoundIcon aria-hidden className="size-6" />}
            title="No sign-ins recorded"
            message="This person's sign-ins to bcecloud will appear here."
          />
        </Panel>
      ) : (
        <>
          <DataTable headers={['When', 'IP address', 'URL']} minWidth={620}>
            {query.data.rows.map((row) => (
              <tr key={row.id} className="border-t border-portal-line">
                <td className="p-3 text-portal-small whitespace-nowrap">
                  {row.at ? new Date(row.at).toLocaleString() : '—'}
                </td>
                <td className="p-3 font-mono text-portal-micro text-portal-muted whitespace-nowrap">
                  {row.ip}
                </td>
                <td className="w-full p-3 font-mono text-portal-micro text-portal-muted">{row.url}</td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            onChange={setPage}
            summary={`${query.data.total} sign-in${query.data.total === 1 ? '' : 's'}`}
          />
        </>
      )}
    </div>
  );
}
